import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Checkbox,
  Collapse,
  Container,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { useForm } from '@mantine/form';
import { useDisclosure, useHotkeys } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconFile,
  IconFileUpload,
  IconMessageQuestion,
  IconX,
} from '@tabler/icons-react';

import { subjectTypeBadgeColor } from '@/constants';
import { useAuth } from '@/contexts/AuthContext';
import { useReportConfigs, useTeams } from '@/hooks';
import { useDirectRequest } from '@/hooks/useDirectRequest';
import { useTeamIntake } from '@/hooks/useTeamIntake';
import { vnextApiClient } from '@/services/vnext-api';
import { Subject } from '@/types';
import type { DirectConfirmCandidate, ReviewCandidate } from '@/types/direct-request';
import type { ClientEnquiry } from '@/types/enquiry';
import type { ReportConfigSummary } from '@/types/report-config';
import { SCREENING_CONFIG_ID } from '@/types/team-settings';
import { RequestReviewList } from './RequestReviewList';
import classes from './RequestReport.module.css';

const ALL_TEAMS = '__all__';

/** Mirror of the backend's ATTACHMENT_EXTENSIONS / build_attachment_blocks
 *  support: spreadsheets, documents, PDFs, images, zips. */
const DROPZONE_ACCEPT = {
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
  'application/json': ['.json'],
  'application/pdf': ['.pdf'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

function DirectResultsView({
  reports,
  onReset,
}: Readonly<{ reports: Record<string, Subject[]>; onReset: () => void }>) {
  const reportEntries = Object.entries(reports);
  const isSeparate = reportEntries.length > 1;

  return (
    <Stack gap="lg" w="100%">
      <Center>
        <Stack align="center" gap="xs">
          <IconCheck size={48} color="var(--mantine-color-green-6)" />
          <Title order={3}>{isSeparate ? 'Reports Requested' : 'Report Requested'}</Title>
        </Stack>
      </Center>

      {reportEntries.map(([reportId, subjects]) => (
        <Stack key={reportId} gap="xs">
          {isSeparate && (
            <Text size="sm" fw={600} c="dimmed">
              Report: {reportId.slice(0, 8)}...
            </Text>
          )}
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Jurisdictions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {subjects.map((subject) => (
                <Table.Tr key={subject.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {subject.subjectName}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      variant="light"
                      color={subjectTypeBadgeColor(subject.subjectType)}
                    >
                      {subject.subjectType}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {subject.jurisdictions.map((j) => j.name).join(', ') || '-'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      ))}

      <Group justify="center" mt="md">
        <Button component={Link} to="/reports" variant="outline">
          View Reports
        </Button>
        <Button onClick={onReset}>Request Another Report</Button>
      </Group>
    </Stack>
  );
}

function TriageSubmittedView({
  candidates,
  subjectLines = [],
  fileCount = 0,
  requestId,
  onReset,
}: Readonly<{
  candidates: ReviewCandidate[];
  /** Async (file-bearing) path: the raw typed lines — no extraction ran. */
  subjectLines?: string[];
  fileCount?: number;
  /** Deep-link to the client Requests tab entry for this submission. */
  requestId?: string;
  onReset: () => void;
}>) {
  const subjects = candidates.flatMap((c) => c.subjects);
  const asyncFiles = subjects.length === 0;
  return (
    <Stack gap="lg" w="100%">
      <Center>
        <Stack align="center" gap="xs">
          <IconCheck size={48} color="var(--mantine-color-green-6)" />
          <Title order={3}>{asyncFiles ? 'Request received' : 'Submitted for review'}</Title>
          <Text size="sm" c="dimmed" ta="center" maw={480}>
            {asyncFiles
              ? "We're reading your files now — no need to wait. An analyst will " +
                'review the request, confirm scope, turnaround and pricing, and ' +
                'start the screening.'
              : 'An analyst will review your request, confirm scope, turnaround and pricing, ' +
                "and start the screening. We'll be in touch shortly."}
          </Text>
        </Stack>
      </Center>

      {asyncFiles ? (
        (subjectLines.length > 0 || fileCount > 0) && (
          <Stack gap={4} align="center">
            {subjectLines.map((line) => (
              <Text key={line} size="sm" fw={500}>
                {line}
              </Text>
            ))}
            {fileCount > 0 && (
              <Text size="sm" c="dimmed">
                {fileCount} file{fileCount === 1 ? '' : 's'} attached
              </Text>
            )}
          </Stack>
        )
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Jurisdictions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {subjects.map((subject, index) => (
              <Table.Tr key={index}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {subject.subject_name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {subject.subject_type ? (
                    <Badge
                      size="sm"
                      variant="light"
                      color={subjectTypeBadgeColor(subject.subject_type)}
                    >
                      {subject.subject_type}
                    </Badge>
                  ) : (
                    <Text size="sm" c="dimmed">
                      -
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{(subject.jurisdiction_codes ?? []).join(', ') || '-'}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Group justify="center" mt="md">
        {requestId && (
          <Button
            component={Link}
            to={`/requests/${requestId}`}
            variant="light"
            data-testid="view-submitted-request"
          >
            Track your request
          </Button>
        )}
        <Button onClick={onReset}>Request Another Report</Button>
      </Group>
    </Stack>
  );
}

function EnquirySubmittedView({
  enquiry,
  absorbedFileCount,
  onOrderReports,
  orderingReports,
  onReset,
}: Readonly<{
  enquiry: ClientEnquiry;
  /** Files that moved onto the enquiry conversation with the submission. */
  absorbedFileCount: number;
  /** Escape hatch: "actually, I want to order reports" (same text).
   *  Absent for enquiry-only accounts — there is nothing to order. */
  onOrderReports?: () => void;
  orderingReports: boolean;
  onReset: () => void;
}>) {
  return (
    <Stack gap="lg" w="100%" data-testid="enquiry-submitted">
      <Center>
        <Stack align="center" gap="xs">
          <IconCheck size={48} color="var(--mantine-color-green-6)" />
          <Title order={3}>We&apos;ve logged your enquiry</Title>
          <Text size="sm" c="dimmed" ta="center" maw={480}>
            {enquiry.codename ? `Reference ${enquiry.codename}. ` : ''}
            Our team will pick it up and reply in the enquiry&apos;s conversation — you can add more
            detail there any time.
            {absorbedFileCount > 0 &&
              ` Your ${absorbedFileCount} file${absorbedFileCount === 1 ? '' : 's'} came along too.`}
          </Text>
        </Stack>
      </Center>

      <Group justify="center" mt="md">
        <Button
          component={Link}
          to={`/requests/enquiries/${enquiry.id}`}
          variant="light"
          data-testid="view-submitted-enquiry"
        >
          View your enquiry
        </Button>
        <Button onClick={onReset} variant="default">
          Done
        </Button>
      </Group>
      {onOrderReports && (
        <Center>
          <Anchor
            component="button"
            type="button"
            size="sm"
            onClick={onOrderReports}
            disabled={orderingReports}
            data-testid="enquiry-order-reports-instead"
          >
            Actually, I want to order reports from this
          </Anchor>
        </Center>
      )}
    </Stack>
  );
}

/** Optional report-type picker below the intake box. Toggle-deselect
 *  cards; the selection is a classifier HINT and, when the message is
 *  classified as an order, the explicit config for the request — it
 *  never skips classification (clients may pick a type while still
 *  only asking a question). */
function ReportConfigCards({
  configs,
  selectedId,
  onSelect,
  showLegacyBadge,
}: Readonly<{
  configs: ReportConfigSummary[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Admin-only: mark manual / Backoffice-docx fulfilment configs. */
  showLegacyBadge: boolean;
}>) {
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} w="100%" className={classes.cardGrid}>
      {configs.map((config) => (
        <Card
          key={config.id}
          padding="md"
          radius="md"
          className={`${classes.card} ${selectedId === config.id ? classes.cardSelected : ''}`}
          // Clicking the selected card again deselects — the pick is
          // optional (unselected → we work it out from the message, or
          // it's chosen in review / triage).
          onClick={() => onSelect(selectedId === config.id ? null : config.id)}
          data-testid={`config-card-${config.slug}`}
        >
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="sm">
                {config.name}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={2} mih={32}>
                {config.description ?? ' '}
              </Text>
            </Stack>
            <IconCheck
              size={18}
              color="var(--mantine-color-yellow-5)"
              style={{
                visibility: selectedId === config.id ? 'visible' : 'hidden',
                flexShrink: 0,
              }}
            />
          </Group>
          {showLegacyBadge && config.legacyFlow && (
            <Group mt="auto" pt="xs">
              <Badge size="xs" variant="light" color="orange" data-testid="legacy-badge">
                Legacy flow
              </Badge>
            </Group>
          )}
        </Card>
      ))}
    </SimpleGrid>
  );
}

export function RequestReport() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdministrator;

  const { teams } = useTeams();
  const [teamFilter, setTeamFilter] = useState<string | null>(null);

  // Compose is deliberately minimal: prose + files. Split ("one report
  // per subject") and expedite belong to the REVIEW step; report-type
  // picking lives in the review's per-candidate picker / analyst triage.
  const [separateReports, setSeparateReports] = useState(true);
  const [expedited, setExpedited] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [sendingEnquiry, setSendingEnquiry] = useState(false);
  const [enquiryResult, setEnquiryResult] = useState<ClientEnquiry | null>(null);
  const [absorbedFileCount, setAbsorbedFileCount] = useState(0);

  const {
    phase,
    draft,
    files,
    isUploading,
    candidates,
    setCandidates,
    needsHumanReview,
    result,
    uploadFiles,
    removeFile,
    extract,
    confirm,
    submitWithFiles,
    backToCompose,
    reset,
  } = useDirectRequest();

  // File-bearing requests take the ASYNC path: no client-side extraction
  // or review — instant submit, analysts drive triage while the files
  // digest in the background.
  const hasFiles = files.length > 0;

  useEffect(() => {
    if (!isAdmin || teamFilter !== null) {
      return;
    }
    if (user?.teamId !== undefined) {
      setTeamFilter(String(user.teamId));
    } else if (teams.length > 0) {
      setTeamFilter(String(teams[0].id));
    }
  }, [isAdmin, teamFilter, user?.teamId, teams]);

  const effectiveTeamId =
    !isAdmin || teamFilter === ALL_TEAMS ? undefined : (teamFilter ?? undefined);
  /** Admin on-behalf-of override sent to the backend. */
  const teamOverride = isAdmin && effectiveTeamId ? effectiveTeamId : undefined;

  // Compose offers an OPTIONAL report-type pick below the box (fed to
  // the classifier as a hint, and used as the explicit config when the
  // message classifies as an order); review keeps its per-candidate
  // picker for anything left unpicked.
  const { configs: reportConfigs } = useReportConfigs({
    isActive: true,
    teamId: effectiveTeamId,
  });

  // The team's intake mode shapes the page for clients (what the box
  // does, whether Screening is offered); admins keep the flexible flow
  // and only inherit the Screening entry when acting for a screening
  // team. Clients resolve their own team; admins the selected one.
  const { intake, isLoading: intakeLoading } = useTeamIntake({
    teamId: isAdmin ? effectiveTeamId : undefined,
    enabled: !isAdmin || Boolean(effectiveTeamId),
  });
  const clientMode = !isAdmin ? (intake?.effectiveMode ?? null) : null;
  const offersScreening = intake?.offersScreening ?? false;

  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  // Collapsed by default — prose is the primary path; the picker is a
  // power-user shortcut behind a disclosure. Screening accounts get it
  // opened with Screening pre-selected instead (see effect below).
  const [configPickerOpened, configPicker] = useDisclosure(false);
  // An admin switching teams invalidates a prior pick (configs are
  // team-scoped; a stale foreign id would 404 the extract call).
  useEffect(() => {
    setSelectedConfigId(null);
  }, [effectiveTeamId]);

  // Screening accounts: Screening is the default selection. With custom
  // types also on offer the picker opens so the default is visible, not
  // hidden behind the disclosure.
  const openConfigPicker = configPicker.open;
  const hasCustomConfigs = reportConfigs.length > 0;
  useEffect(() => {
    if (!offersScreening) {
      return;
    }
    setSelectedConfigId(SCREENING_CONFIG_ID);
    if (hasCustomConfigs) {
      openConfigPicker();
    }
  }, [offersScreening, hasCustomConfigs, openConfigPicker]);

  const screeningEntry = {
    id: SCREENING_CONFIG_ID,
    slug: 'screening',
    name: 'Screening',
    description: 'Sanctions, watchlists and adverse media checks on each subject.',
  } as ReportConfigSummary;
  const pickerConfigs = offersScreening ? [screeningEntry, ...reportConfigs] : reportConfigs;
  const selectedConfig = pickerConfigs.find((c) => c.id === selectedConfigId) ?? null;
  /** The pick as the API understands it — Screening is not a config. */
  const apiConfigId = selectedConfigId === SCREENING_CONFIG_ID ? null : selectedConfigId;
  /** Screening pinned: the explicit Screening pick, or a screening-mode
   *  client with nothing picked (their box defaults to Screening). */
  const screeningPinned =
    offersScreening &&
    (selectedConfigId === SCREENING_CONFIG_ID || (!isAdmin && selectedConfigId === null));

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      subjectsText: '',
    },
  });

  const handleExtract = async (opts?: { textOnly?: boolean }) => {
    const text = form.values.subjectsText.trim();
    try {
      const extracted = await extract(text, teamOverride, apiConfigId, {
        ...opts,
        screeningPinned,
      });
      // Normalise for review: seed each candidate's attachment picks from
      // provenance (falling back to every extraction file when provenance
      // is empty). Explicit compose-page pick wins over the LLM's derived
      // config (the endpoint post-stamps too — belt and braces); a pinned
      // Screening pick keeps candidates config-less.
      const uploadedIds = new Set(files.map((f) => f.id));
      setCandidates(
        extracted.map((c) => {
          const provenanceIds = (c.sources ?? [])
            .map((s) => s.conversation_attachment_id)
            .filter((id): id is string => Boolean(id && uploadedIds.has(id)));
          return {
            ...c,
            report_config_id: screeningPinned ? null : (apiConfigId ?? c.report_config_id ?? null),
            attachment_ids: opts?.textOnly ? [] : provenanceIds,
          };
        })
      );
    } catch {
      // Error notification is handled by the hook
    }
  };

  const handleSendAsEnquiry = async () => {
    const text = form.values.subjectsText.trim();
    setSendingEnquiry(true);
    try {
      const enquiry = await vnextApiClient.createClientEnquiry({
        text,
        draftId: hasFiles ? draft?.report_request_id : undefined,
        teamId: teamOverride ?? null,
      });
      setAbsorbedFileCount(files.length);
      setEnquiryResult(enquiry);
    } catch (err) {
      notifications.show({
        title: "We couldn't log your enquiry",
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'red',
      });
    } finally {
      setSendingEnquiry(false);
    }
  };

  /** Route the submission. Mode-driven accounts are deterministic:
   *  enquiry-only → always an enquiry; screening accounts → always a
   *  report order (never an enquiry). Everyone else: classify the
   *  prose, then enquiry → /client/enquiries; report order → async
   *  submit (files) or extract → review (text). */
  const handlePrimary = async () => {
    const text = form.values.subjectsText.trim();
    if (!text && !hasFiles) {
      form.setFieldError('subjectsText', 'Tell us what you need — or attach files');
      return;
    }

    if (clientMode === 'enquiry_only') {
      // The box always raises an enquiry — and an enquiry needs words
      // (the endpoint rejects empty text; files ride along with it).
      if (!text) {
        form.setFieldError('subjectsText', 'Tell us what you need in a few words');
        return;
      }
      await handleSendAsEnquiry();
      return;
    }

    if (!text) {
      // Files with no prose: nothing to classify, and the enquiry endpoint
      // rejects empty text. A files-only submission is unambiguously a
      // report order — triage extracts the subjects from the attachments.
      await handleSubmitAsync();
      return;
    }

    if (clientMode === 'screening_only' || clientMode === 'screening_and_custom') {
      // Screening accounts never route to enquiry: the box orders
      // reports (Screening by default, or the picked custom type). The
      // classifier is skipped — the backend short-circuits it for these
      // accounts anyway.
      if (hasFiles) {
        await handleSubmitAsync();
      } else {
        await handleExtract();
      }
      return;
    }

    setClassifying(true);
    let kind: 'report_request' | 'enquiry' = 'enquiry';
    try {
      const classification = await vnextApiClient.classifyIntake({
        text,
        draftId: hasFiles ? draft?.report_request_id : undefined,
        // The report-type pick is a HINT, never a bypass: a client can
        // select a type and still be asking a question.
        reportConfigId: apiConfigId,
      });
      kind = classification.kind;
    } catch {
      // Transport failure — the locked bias says treat as enquiry; an
      // analyst can always convert it into requests.
      kind = 'enquiry';
    } finally {
      setClassifying(false);
    }

    if (kind === 'enquiry') {
      await handleSendAsEnquiry();
      return;
    }
    if (hasFiles) {
      await handleSubmitAsync();
    } else {
      await handleExtract();
    }
  };

  /** Escape hatch off the enquiry confirmation: same text, back into the
   *  ordering flow. Any files stay on the enquiry (its draft was
   *  absorbed), so this continues text-only. */
  const handleOrderReportsInstead = async () => {
    setEnquiryResult(null);
    setAbsorbedFileCount(0);
    reset();
    await handleExtract({ textOnly: true });
  };

  const handleConfirm = async () => {
    const confirmCandidates: DirectConfirmCandidate[] = candidates.map((c) => ({
      report_config_id: c.report_config_id ?? undefined,
      subjects: c.subjects,
      jurisdictions: c.jurisdictions,
      expedited: c.expedited,
      attachment_ids: c.attachment_ids,
      sources: c.sources,
    }));
    try {
      await confirm({
        candidates: confirmCandidates,
        separateReports,
        expedited,
        teamId: teamOverride,
      });
    } catch {
      // Error notification is handled by the hook
    }
  };

  const handleSubmitAsync = async () => {
    try {
      await submitWithFiles({
        text: form.values.subjectsText.trim(),
        reportConfigId: apiConfigId,
        expedited: false,
      });
    } catch {
      // Error notification is handled by the hook
    }
  };

  const handleReset = () => {
    setSeparateReports(true);
    setExpedited(false);
    setEnquiryResult(null);
    setAbsorbedFileCount(0);
    setSelectedConfigId(null);
    configPicker.close();
    form.reset();
    reset();
  };

  const isExtracting = phase === 'extracting';
  const isSubmitting = phase === 'submitting';
  const busy = classifying || sendingEnquiry;
  const inReview = !enquiryResult && (phase === 'review' || (isSubmitting && !hasFiles));

  const confirmDisabled =
    isSubmitting ||
    candidates.length === 0 ||
    candidates.some(
      (c) => c.subjects.length === 0 || c.subjects.some((s) => !s.subject_name.trim())
    ) ||
    // When the team HAS configs, every request needs one picked before
    // confirm (the LLM may not have derived one) — unless Screening is
    // on offer, where a config-less candidate IS the screening pick.
    (reportConfigs.length > 0 && !offersScreening && candidates.some((c) => !c.report_config_id));

  useHotkeys([
    [
      'mod+Enter',
      () => {
        if (enquiryResult) {
          return;
        }
        if (phase === 'compose' && !isUploading && !busy) {
          handlePrimary();
        } else if (phase === 'review' && !confirmDisabled) {
          handleConfirm();
        }
      },
    ],
  ]);

  if (enquiryResult) {
    return (
      <div className={classes.wrapper}>
        <Container size="sm" w="100%">
          <EnquirySubmittedView
            enquiry={enquiryResult}
            absorbedFileCount={absorbedFileCount}
            onOrderReports={clientMode === 'enquiry_only' ? undefined : handleOrderReportsInstead}
            orderingReports={isExtracting}
            onReset={handleReset}
          />
        </Container>
      </div>
    );
  }

  if (phase === 'submitted' && result) {
    return (
      <div className={classes.wrapper}>
        <Container size="sm" w="100%">
          {result.mode === 'direct' ? (
            <DirectResultsView reports={result.reports} onReset={handleReset} />
          ) : (
            <TriageSubmittedView
              candidates={candidates}
              subjectLines={form.values.subjectsText
                .split(/\r?\n/)
                .map((s) => s.trim())
                .filter(Boolean)}
              fileCount={files.length}
              requestId={result.reportRequestIds[0]}
              onReset={handleReset}
            />
          )}
        </Container>
      </div>
    );
  }

  if (inReview) {
    return (
      <div className={classes.wrapper}>
        <Container size="md" w="100%">
          <Stack gap="lg" align="center">
            <Stack gap="xs" align="center">
              <Title order={2} ta="center">
                Review your request
              </Title>
              <Text size="sm" c="dimmed" ta="center" maw={520}>
                Check the extracted details and correct anything that&apos;s wrong before
                submitting.
              </Text>
              {needsHumanReview && (
                <Badge color="yellow" variant="light">
                  Some details need your attention
                </Badge>
              )}
            </Stack>

            <RequestReviewList
              candidates={candidates}
              onChange={setCandidates}
              configs={reportConfigs}
              files={files}
              markLegacy={isAdmin}
              offersScreening={offersScreening}
            />

            <Stack gap="xs" w="100%">
              <Checkbox
                label="One report per subject"
                description="Generate a separate report for each subject instead of one combined report"
                checked={separateReports}
                onChange={(event) => setSeparateReports(event.currentTarget.checked)}
                data-testid="review-separate-reports"
              />
              <Checkbox
                label="Expedite this request"
                description="Flag as urgent — an analyst will confirm turnaround time and price"
                checked={expedited}
                onChange={(event) => setExpedited(event.currentTarget.checked)}
                data-testid="review-expedited"
              />
            </Stack>

            {(files.length > 0 || expedited) && (
              <Text size="xs" c="dimmed" ta="center">
                Requests with files or expedited requests are reviewed by an analyst before
                screening begins. The &quot;one report per subject&quot; setting is decided during
                that review.
              </Text>
            )}

            <Group justify="space-between" w="100%">
              <Button variant="default" onClick={backToCompose} disabled={isSubmitting}>
                Back
              </Button>
              <Group gap="sm">
                <Button
                  variant="subtle"
                  color="gray"
                  leftSection={<IconMessageQuestion size={16} />}
                  onClick={() => void handleSendAsEnquiry()}
                  loading={sendingEnquiry}
                  disabled={isSubmitting}
                  data-testid="review-send-as-enquiry"
                >
                  Not ordering reports? Send as an enquiry
                </Button>
                <Button onClick={handleConfirm} loading={isSubmitting} disabled={confirmDisabled}>
                  Confirm Request
                </Button>
              </Group>
            </Group>
          </Stack>
        </Container>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <Container size="sm" w="100%">
        <Stack gap="xl" align="center">
          <Stack gap="xs" align="center" w="100%">
            <Title order={2} ta="center">
              Tell us what you need
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              Order screening reports, ask a question, or request a quote — in your own words.
              Attach files if you have them.
            </Text>
          </Stack>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handlePrimary();
            }}
            style={{ width: '100%' }}
          >
            <Stack>
              {isAdmin && (
                <Group justify="flex-end" gap="xs" align="center" wrap="nowrap">
                  <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    Team:
                  </Text>
                  <Select
                    aria-label="Request on behalf of team"
                    data={[
                      { value: ALL_TEAMS, label: 'All teams' },
                      ...teams.map((t) => ({ value: String(t.id), label: t.name })),
                    ]}
                    value={teamFilter}
                    onChange={setTeamFilter}
                    searchable
                    size="xs"
                    w={220}
                  />
                </Group>
              )}

              <Textarea
                minRows={5}
                maxRows={14}
                autosize
                placeholder={
                  'e.g. "Please run enhanced due diligence on John Smith (UK, CEO of ACME ' +
                  'Corp) and Acme Corporation (US)" — or "What would a Level 2 check on a ' +
                  'Kenyan supplier cost, and how long would it take?"'
                }
                data-demo="subjects-textarea"
                data-testid="intake-textarea"
                {...form.getInputProps('subjectsText')}
              />

              <Dropzone
                onDrop={(dropped) => uploadFiles(dropped, teamOverride)}
                accept={DROPZONE_ACCEPT}
                loading={isUploading}
                multiple
                p="sm"
                radius="md"
                aria-label="Attach files"
              >
                <Group justify="center" gap="xs" style={{ pointerEvents: 'none' }}>
                  <IconFileUpload size={18} color="var(--mantine-color-dimmed)" />
                  <Text size="sm" c="dimmed">
                    Attach files — subject lists, registries, passports, or supporting documents
                  </Text>
                </Group>
              </Dropzone>

              {hasFiles && (
                <Stack gap={4}>
                  {files.map((file) => (
                    <Group key={file.id} gap="xs" wrap="nowrap">
                      <IconFile size={14} />
                      <Text size="sm" style={{ flex: 1 }} truncate>
                        {file.filename}
                      </Text>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => removeFile(file.id)}
                        aria-label={`Remove ${file.filename}`}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              )}

              {/* Below the box on purpose: the lowest-friction route is
                  to just write the message. The pick never bypasses
                  classification — it's a hint, and the explicit config
                  once the message classifies as an order. Collapsed by
                  default; a pick made earlier stays visible in the
                  header so no selection hides behind a closed panel. */}
              {reportConfigs.length > 0 && clientMode !== 'enquiry_only' && (
                <Stack gap="xs" mt="xs">
                  <UnstyledButton
                    onClick={configPicker.toggle}
                    aria-expanded={configPickerOpened}
                    aria-controls="report-type-picker"
                    data-testid="report-type-toggle"
                  >
                    <Group gap="xs" wrap="nowrap">
                      {configPickerOpened ? (
                        <IconChevronDown size={16} />
                      ) : (
                        <IconChevronRight size={16} />
                      )}
                      <Text fw={600} size="sm">
                        Report type{' '}
                        {!offersScreening && (
                          <Text component="span" c="dimmed" fw={400}>
                            (optional)
                          </Text>
                        )}
                      </Text>
                      {!configPickerOpened && selectedConfig && (
                        <Badge size="sm" variant="light" data-testid="report-type-summary">
                          {selectedConfig.name}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={configPickerOpened}>
                    <Stack gap="xs" id="report-type-picker">
                      <Text size="xs" c="dimmed">
                        {offersScreening
                          ? 'Screening is selected by default — pick a report type instead if you need one.'
                          : "Pick one if you already know what you need — otherwise leave it to us. If you're asking a question rather than placing an order, we'll still treat it that way."}
                      </Text>
                      <ReportConfigCards
                        configs={pickerConfigs}
                        selectedId={selectedConfigId}
                        onSelect={setSelectedConfigId}
                        showLegacyBadge={isAdmin}
                      />
                    </Stack>
                  </Collapse>
                </Stack>
              )}

              <Group justify="flex-end" align="center">
                <Button
                  type="submit"
                  loading={busy || isExtracting || (isSubmitting && hasFiles)}
                  disabled={
                    busy ||
                    isExtracting ||
                    isSubmitting ||
                    isUploading ||
                    // Clients wait for their intake mode — routing before
                    // it resolves could send a screening client to the
                    // classifier (or an enquiry client to extraction).
                    (!isAdmin && intakeLoading)
                  }
                  data-testid="request-primary"
                >
                  Continue
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Container>
    </div>
  );
}
